import * as lodash from 'lodash'
import { isNil } from './nil.no-deps'
import { compose } from './compose.no-deps'
import { not } from './boolean.no-deps'
import { field } from './object.no-deps'
import { zipEvery, zipIterator } from './zip.no-deps'
import { groupBy } from 'lodash'
import { dictMap, type Dictionary } from './dictionary.no-deps'
import { identity } from './function.no-deps'

/**
 * Partial application of `array.map` to be used in `pipe` e.g.
 *
 *    pipe(
 *        ['hello', 'strange', 'beautiful', 'world'],
 *        map(s => s.length),                // 5, 7, 9, 5
 *        map(n => n * n - n),               // 20, 42, 72, 20
 *        map(n => (n > 30 ? `${n}` : null)) // null, '42', '72', null
 *    )
 */
export function map<A, B>(fn: (member: A, index: number, array: A[]) => B) {
    return (array: A[]): B[] => array.map(fn)
}

/**
 * Partial application of `array.reduce` to be used in `pipe` (with order of parameters reversed) e.g.
 *
 *   pipe(
 *       ['a', 'b', 'c', 'd'],
 *       reduce('', (ls, l) => `${ls}${ls}${l} `), // "a a b a a b c "
 *       str => str.split(' '),
 *       reduce(0, count => count + count + 1) // 255
 *   )
 */
export function reduce<A, B>(initial: B, fn: (accumulator: B, member: A, index: number) => B) {
    return (array: A[]): B => array.reduce(fn, initial)
}

/**
 * Partial application of `array.filter` to be used in `pipe` e.g.
 *
 *    pipe(
 *        range(1, 100),
 *        filter(n => Math.pow(-n, n) % 5 === 0),
 *        filter(n => n + n > 75),
 *        consoleLog // 42, 47, 51, 52, 68, 84, 95
 *    )
 */
export function filter<T, A extends T>(fn: (m: T) => m is A): (array: T[]) => A[]
export function filter<T>(fn: (m: T) => boolean): (array: T[]) => T[]
export function filter<T>(fn: (m: T) => boolean) {
    return (array: T[]): T[] => array.filter(fn)
}

/**
 * Inverse of `filter` to be used in `pipe` e.g.
 *
 *    pipe(
 *        range(1, 100),
 *        filterOut(n => Math.pow(-n, n) % 5 === 0),
 *        filterOut(n => n + n > 75),
 *        filterOut(isEven),
 *        consoleLog
 *        // 1,  3,  7,  9, 11, 13, 15, 17, 19, 21, 23, 27, 29, 31, 33, 35, 37
 *    )
 */
export function filterOut<T>(fn: (m: T) => boolean) {
    const notFn = compose(fn, not)
    return (array: T[]): T[] => array.filter(notFn)
}

export function every<T>(fn: (m: T) => boolean) {
    return (array: T[]): boolean => array.every(fn)
}

export function some<T>(fn: (m: T) => boolean) {
    return (array: T[]): boolean => array.some(fn)
}

/**
 * Partial application of `array.find` to be used in `pipe` e.g.
 *
 *    pipe(
 *        [{ name: 'a' }, { name: 'b' }, { name: 'c' }],
 *        find(person => person.name === 'b') // { name: 'b' }
 *    )
 */
export function find<T>(fn: (value: T, index: number) => boolean) {
    return (array: T[]): T | undefined => array.find(fn)
}

/**
 * Partial application of `array.slice` to be used in `pipe` e.g.
 *
 *    pipe(
 *        [5, 7, 9, 1, 3, 5, 7],
 *        slice(3, 7) // [1, 3, 5, 7]
 *    )
 */
export function slice(start: number, end?: number) {
    return <T>(array: T[]): T[] => array.slice(start, end)
}

/**
 * Partial application of `array.concat` to be used in `pipe` e.g.
 *
 *    pipe(
 *        [1, 2, 3, 4],
 *        append(5, 6, 7) // [1, 2, 3, 4, 5, 6, 7]
 *    )
 */
export function append<T>(...b: T[]) {
    return (a: T[]): T[] => concat(a, b)
}

export function concat<T>(a: T[], b: T[]): T[] {
    return [...a, ...b]
}

export function flatten<T>(array: T[][]): T[] {
    return array.flatMap(identity)
}

export function uniq<T>(array: T[]): T[] {
    return Array.from(new Set(array))
}

/**
 * A variant of uniq that scarifies speed to guarantee stability
 *
 * Use when it is important that items from the resulting array are returned
 * in the same order as the input array.
 */
export function uniqStable<T>(array: T[]): T[] {
    return array.reduce<T[]>((acc, item) => (acc.includes(item) ? acc : acc.concat(item)), [])
}

export const compact = filterOut(isNil)

export function clone<T>(array: T[]): T[] {
    return [...array]
}

export function join(separator: string) {
    return (array: string[]): string => array.join(separator)
}

export function reverse<T>(array: T[]): T[] {
    return clone(array).reverse()
}

export function sort<T>(array: T[], fn: (a: T, b: T) => number) {
    return clone(array).sort(fn)
}

export type Comparison = 'left-first' | 'same' | 'right-first'

export function friendlySort<T>(array: T[], fn: (a: T, b: T) => Comparison) {
    return sort(array, (a, b) => {
        switch (fn(a, b)) {
            case 'left-first':
                return -1
            case 'same':
                return 0
            case 'right-first':
                return 1
        }
    })
}

export function sortByFieldAlphabetically<T extends { [k in K]: string }, K extends string>(field: K) {
    return (array: T[]) => sort<T>(array, (a, b) => a[field].localeCompare(b[field]))
}

export function at<T>(index: number) {
    return (array: T[]) => array[index]
}

export function head<T>(array: T[]) {
    return array[0]
}

export function last<T>(array: T[]) {
    return array[array.length - 1]
}

/**
 * Returns an array of consecutive numbers between two numbers
 *
 *     range(5, 8) // [5, 6, 7, 8]
 */
export function range(from: number, to: number): number[] {
    return Array<number | null>(to + 1 - from)
        .fill(null)
        .map((_, index) => index + from)
}

/**
 * Maps over an array and gets the value of a field e.g.
 *
 *     const rects = [
 *         { x: 34, y: 48},
 *         { x: 19, y: 59},
 *         { x: 49, y: 10002 },
 *     ]
 *     mapField('x')(rects) // [34, 19, 49]
 */
export const mapField = compose(field, map)

export function arrayPick<T>(array: T[], ...indexes: number[]) {
    return indexes.map((index) => array[index])
}

export function uniqWith<T>(isEqual: (a: T, b: T) => boolean) {
    return (array: T[]) => {
        const newArray: T[] = []

        for (const b of array) {
            const alreadyAdded = newArray.some((a) => isEqual(a, b))
            if (alreadyAdded) continue

            newArray.push(b)
        }

        return newArray
    }
}

export function isEmpty<T>(array: T[]) {
    return array.length === 0
}

/**
 * Like `filter` but the predicate function is asynchronous.
 *
 *    pipe(
 *        range(1, 100),
 *        filterAsync(async n => n < 5),
 *        promiseThen(consoleLog) // 1, 2, 3, 4, 5
 *    )
 */
export function filterAsync<T>(fn: (m: T) => Promise<boolean>) {
    return async (array: T[]): Promise<T[]> => {
        const results = await Promise.all(array.map(fn))
        return array.filter((_, i) => results[i])
    }
}

/**
 * Like `map` but the mapping function is asynchronos.
 * The mappings are executed concurrently.
 *
 *    pipe(
 *        range(1, 100),
 *        mapAsync(async n => n + 5),
 *        promiseThen(consoleLog) // 5, 6, 7, 8, 9 ...etc
 *    )
 */
export function mapAsync<A, B>(fn: (a: A) => Promise<B>) {
    return async (array: A[]): Promise<B[]> => {
        return Promise.all(array.map(fn))
    }
}

/**
 * Returns true if all the values in the array are the same
 *
 *     allSameValue([1, 1, 1, 1, 1]) // true
 *     allSameValue([1, 1, 1, 0, 1]) // false
 */
export function allSameValue<T>(array: T[]): boolean {
    return array.every((m) => m === array[0])
}

export function allSameLength<T>(arrays: T[][]): boolean {
    return allSameValue(arrays.map((a) => a.length))
}

/**
 * Compare that arrays are equal using a comparator function
 *
 *    const isEqual = ([a, b]: number[]) => Math.abs(a) === Math.abs(b)
 *    equalBy([[1, -2, -3, 4], [1, -2, 3, -4]], isEqual) // true
 */
export function equalBy<T>(arrays: T[][], isEqual: (array: T[]) => boolean): boolean {
    return allSameLength(arrays) && zipEvery(arrays, isEqual)
}

/**
 * If you're not sure if an array exists and you want to either initialize it or just add to it
 */
export function concatOrSingleton<T>(array: T[] | undefined, value: T): T[] {
    return array == null ? [value] : array.concat(value)
}

export function singleton<T>(value: T): T[] {
    return [value]
}

/**
 * Insert a value between all other values in the array
 *
 *    interleave([1, 2, 3], 5) // [1, 5, 2, 5, 3]
 */
export function interleave<T>(array: T[], value: T): T[] {
    const second = Array(array.length).fill(value)
    const zipped = Array.from(zipIterator([array, second]))
    const result = flatten(zipped)
    result.pop()
    return result
}

/**
 * Curried version of interleave for use in pipes
 */
export function interleaveWith<T>(value: T): (array: T[]) => T[] {
    return function (array: T[]) {
        return interleave(array, value)
    }
}

/**
 * Curried version of groupBy for use in pipes
 */
export function groupWith<T>(groupFn: (t: T) => string): (array: T[]) => Dictionary<T[]> {
    return (array) => groupBy(array, groupFn)
}

/**
 * groupWith followed by a nested map
 */
export function groupWithAndMap<T, U>(groupFn: (t: T) => string, mapFn: (t: T) => U): (array: T[]) => Dictionary<U[]> {
    return compose(groupWith(groupFn), (d) => dictMap(d, map(mapFn)))
}

export function omit<T extends object, K extends keyof T>(...keys: K[]) {
    return (array: T[]): Array<Omit<T, K>> => array.map((m) => lodash.omit(m, keys))
}

export function difference<T>(b: T[]) {
    return (a: T[]): T[] => a.filter((m) => !b.includes(m))
}

export function throwIfEmpty<T>(fn: () => Error) {
    return (array: T[]): T[] => {
        if (array.length === 0) throw fn()
        return array
    }
}

export function throwUnlessSingleton<T>(fn: () => Error) {
    return (array: T[]): T[] => {
        if (array.length !== 1) throw fn()
        return array
    }
}
